/**
 * Auth Module Types
 *
 * Provider-agnostic types for the auth abstraction layer.
 * The AuthProvider interface defines the adapter contract;
 * implementations live in adapters/.
 */

// ─── Domain types ────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
}

/**
 * Provider-agnostic session.
 * `raw` holds the provider's native session object for layers
 * that need direct token access (API interceptors, socket auth).
 */
export interface AuthSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  /** Provider's raw session object — opaque to the auth module */
  raw: unknown;
}

export type AuthEventType =
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY';

export interface AuthResult {
  user: AuthUser;
  session: AuthSession;
}

export interface SignUpResult {
  user: AuthUser;
  session: AuthSession | null;
  emailConfirmationRequired: boolean;
}

// ─── Provider adapter interface ──────────────────────────────────

export interface AuthProvider {
  /** Email + password sign-in */
  signIn(email: string, password: string): Promise<AuthResult>;

  /** Email + password sign-up with optional user metadata */
  signUp(
    email: string,
    password: string,
    metadata?: Record<string, unknown>,
  ): Promise<SignUpResult>;

  /** Sign out the current user */
  signOut(): Promise<void>;

  /** Read the stored session (from memory/storage, no network call) */
  getSession(): Promise<{ user: AuthUser; session: AuthSession } | null>;

  /** Force-refresh the session token (network call) */
  refreshSession(): Promise<AuthSession | null>;

  /** Send password-reset email */
  resetPassword(email: string, options?: { redirectTo?: string }): Promise<void>;

  /**
   * Verify the emailed 6-digit recovery code (typed-OTP password reset).
   * On success the provider establishes a recovery session — same session
   * shape the reset deep link produces.
   */
  verifyRecoveryCode(email: string, code: string): Promise<AuthResult>;

  /**
   * Verify the emailed 6-digit sign-up confirmation code (typed-OTP
   * email confirmation, AUTH-013). On success the provider establishes a
   * full session — the user just proved the email and is signed in.
   */
  verifySignupCode(email: string, code: string): Promise<AuthResult>;

  /**
   * Re-send the sign-up confirmation email. Unlike recovery (which
   * resends via resetPassword), signup resend needs its own provider call.
   */
  resendSignupConfirmation(email: string): Promise<void>;

  /** Set a new password (after reset link/code or from profile) */
  updatePassword(newPassword: string): Promise<void>;

  /** Update provider-level user metadata (e.g. display name) */
  updateUserMetadata(data: Record<string, unknown>): Promise<void>;

  /**
   * Subscribe to auth state changes from the provider.
   * Returns an unsubscribe function.
   */
  onAuthStateChange(
    callback: (event: AuthEventType, user: AuthUser | null, session: AuthSession | null) => void,
  ): () => void;

  /**
   * Get the current access token (for API/socket layers).
   * Reads from memory — no network call.
   */
  getAccessToken(): Promise<string | null>;
}

// ─── Provider error ──────────────────────────────────────────────

/**
 * Provider-agnostic auth error codes.
 * Adapters map their provider's native errors to these codes.
 * Each code maps to an i18n key under `errors.auth.*`.
 */
export type AuthErrorCode =
  // Login
  | 'INVALID_CREDENTIALS'
  | 'USER_NOT_FOUND'
  | 'EMAIL_NOT_CONFIRMED'
  // Registration
  | 'USER_ALREADY_EXISTS'
  | 'WEAK_PASSWORD'
  // Rate limiting
  | 'RATE_LIMITED'
  // Password
  | 'SAME_PASSWORD'
  | 'RESET_LINK_EXPIRED'
  | 'RECOVERY_CODE_INVALID'
  // Sign-up confirmation
  | 'SIGNUP_CODE_INVALID'
  // Session
  | 'SESSION_EXPIRED'
  // Catch-all
  | 'UNKNOWN';

/**
 * Normalized auth error thrown by adapters.
 * The store catches these and uses `.message` directly — no provider-specific parsing.
 */
export class AuthProviderError extends Error {
  readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode, message: string) {
    super(message);
    this.name = 'AuthProviderError';
    this.code = code;
  }
}

// ─── Machine types ───────────────────────────────────────────────

export type AuthMachineState =
  | 'idle'
  | 'restoring'
  | 'authenticating'
  | 'authenticated'
  | 'loggingOut';
